import React, { useEffect } from 'react'
import { Container, Dialog, DialogContent, DialogTitle, DialogActions, Button, Stack } from '@mui/material'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { yupResolver } from '@hookform/resolvers/yup/dist/yup'
import { trainingSchema } from '../validators'
import { RoutineType } from '../../../../domain/db/RoutineTypeDB'
import { WeekdaysDB } from '../../../../domain/db/WeekdaysDB'
import { NumericDB } from '../../../../domain/db/NumericDB'
import api from '../../../../services/api'
import { Select } from '../../../../components/Select'
import { TextArea } from '../../../../components/TextArea'
import { LoadingButton } from '@mui/lab'
import { FormInputText } from 'components/FormInputText'
import { useAppDispatch } from 'redux/hooks'
import { showMessage } from 'redux/reducers/snackbar/snackbar.actions'

type TrainingFormProps = {
  id: string
  name: string
  sequence: number
  note?: string
  routineId?: string
}
export const TrainingForm = ({ isVisible, training, onClose, onSubmitted, onCancel, loading, routine }: any) => {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const { control, handleSubmit, reset, setValue, formState: { errors } } = useForm<TrainingFormProps>({
    resolver: yupResolver(trainingSchema)
  })

  useEffect(() => {
    if (isVisible) {
      reset()
    }
    dataFetch().catch(console.error)
  }, [isVisible])

  const dataFetch = async () => {
    if (training == null) return
    const fields = ['id', 'name', 'sequence', 'note']
    fields.forEach((field: any) => setValue(field, training[field]))
  }

  const getTypeData = () => {
    if (routine?.type === RoutineType.WEEKDAYS) return WeekdaysDB()
    return NumericDB()
  }

  const getDefaultValue = () => {
    if (routine?.type === RoutineType.WEEKDAYS) return 2
    return 1
  }

  const handleUpInsertTraining = async (training: TrainingFormProps): Promise<void> => {
    try {
      let saveMessage = t('training.create_success')
      if (training.id !== undefined) {
        saveMessage = t('training.update_success')
        await api.put(`trainings/${training.id}`, training)
      } else {
        training.routineId = routine.id
        await api.post('trainings', training)
      }
      onSubmitted()
      dispatch(showMessage({ message: saveMessage }))
    } catch (e: any) {
      dispatch(showMessage({ message: `${t('general.error')}`, type: 'error' }))
    }
  }

  return (
    <Dialog
      fullWidth={true}
      maxWidth="sm"
      open={isVisible}
      onClose={onClose}
      aria-labelledby="customized-dialog-title"
    >
      <DialogTitle>{(training ? t('training.editTraining') : t('training.newTraining'))}</DialogTitle>
      <DialogContent>
        <Container component="main">
          <Stack sx={{ mt: 3 }}>
            <Controller
              control={control}
              name="sequence"
              defaultValue={getDefaultValue()}
              render={({ field: { onChange, value } }) => (
                <Select
                  value={String(value)}
                  onChange={onChange}
                  placeholder={`${t('general.validator.option')}`}
                  label={`${t('training.field.sequence.label')}`}
                  items={getTypeData()}
                  errorMessage={''}
                />
              )} />
          </Stack>
          <Stack sx={{ mt: 3 }}>
            <Controller
              control={control}
              name="name"
              defaultValue=""
              render={({ field: { onChange, value } }) => (
                <FormInputText
                  required
                  name="name"
                  control={control}
                  value={value}
                  placeholder={`${t('training.field.name.placeholder')}`}
                  label={`${t('training.field.name.label')}`}
                  onChange={onChange}
                  errorMessage={errors.name?.message}
                />
              )} />
          </Stack>
          <Stack sx={{ mt: 3 }}>
            <Controller
              control={control}
              name="note"
              defaultValue=""
              render={({ field: { onChange, value } }) => (
                <TextArea
                  value={value}
                  label={`${t('training.field.note.label')}`}
                  placeholder={`${t('training.field.note.placeholder')}`}
                  onChange={onChange}
                />
              )} />
          </Stack>
        </Container>
      </DialogContent>
      <DialogActions>
        <LoadingButton loading={loading} onClick={handleSubmit(handleUpInsertTraining)} variant="contained">
          {t('general.btn.save')}
        </LoadingButton>
        <Button variant="contained" color="secondary" onClick={() => onCancel()}>
          {t('general.btn.dismiss')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
